import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import animateScrollTo from "animated-scroll-to";
import IconsPage from "./IconsPage";
import { searchIcons } from "../utils/searchIcons";
import { addCategory, removeCategory, emptyCategory, fetchCategory } from "../Actions/iconAction";

// The server preloads the chosen categories' icons from this cookie
const rememberCategories = (names) => {
  document.cookie = `selectedCategories=${JSON.stringify(names)}; path=/`;
};

/**
 * State and actions for the icons page: which categories are shown, the search, the
 * style filter and how many category sections have loaded so far.
 */
// Links from the homepage: ?q= starts a search, ?category= opens one category
const readParam = (location, key) => {
  if (!location || !location.search) return "";
  return new URLSearchParams(location.search).get(key) || "";
};

const IconsPageContainer = ({ localstore, selectedNames, settings, dispatch, location, history }) => {
  const [searchTxt, setSearchTxt] = useState(() => readParam(location, "q"));
  const [styleFilter, setStyleFilterState] = useState("all"); // "all", "line", "solid" or "duotone"
  // Category sections rendered so far; more load as the placeholder scrolls into view
  const [limit, setLimit] = useState(2);

  useEffect(() => {
    dispatch(fetchCategory());
    const category = readParam(location, "category");
    if (category && localstore.filter_category.some((cat) => cat.cat_name === category)) {
      dispatch(emptyCategory());
      dispatch(addCategory(category));
      rememberCategories([category]);
    }
    // The link has done its job; searches and categories change from here on. Other
    // parameters (?icon= opens an icon's panel) stay
    if (history && (readParam(location, "q") || category)) {
      const params = new URLSearchParams(location.search);
      params.delete("q");
      params.delete("category");
      const rest = params.toString();
      history.replace(location.pathname + (rest ? "?" + rest : ""));
    }
  }, []);

  const allCategories = localstore.filter_category;
  const categoryList = useMemo(
    () => allCategories.map((cat) => ({ ...cat, checked: selectedNames.includes(cat.cat_name) })),
    [allCategories, selectedNames]
  );
  // Chosen categories, newest first; all of them when none is chosen
  const iconCategoryList = useMemo(() => {
    const chosen = selectedNames
      .map((name) => categoryList.find((cat) => cat.cat_name === name))
      .filter(Boolean);
    return chosen.length ? chosen : categoryList;
  }, [categoryList, selectedNames]);

  const searching = searchTxt.trim().length > 0;
  const results = useMemo(
    () => (searching ? searchIcons(localstore.icons, searchTxt) : []),
    [searching, localstore.icons, searchTxt]
  );

  const onCategoryChange = (catIndex) => {
    const category = categoryList[catIndex];
    const names = category.checked
      ? selectedNames.filter((name) => name !== category.cat_name)
      : [category.cat_name, ...selectedNames];
    dispatch(category.checked ? removeCategory(category.cat_name) : addCategory(category.cat_name));
    rememberCategories(names);
    setLimit(1);
    animateScrollTo(10, { element: window, speed: 1000 });
  };

  const clearCatstore = () => {
    dispatch(emptyCategory());
    rememberCategories([]);
    setLimit(1);
  };

  // Search as you type; an empty query goes back to the category view
  const onSearch = (value) => {
    setSearchTxt(value);
    if (value.trim()) setLimit(1);
  };

  const state = {
    categoryList,
    iconCategoryList,
    searchSwitch: searching,
    searchTxt,
    searchIcons: results,
    isIconReady: allCategories.length > 0,
    limit,
    styleFilter,
  };

  return (
    <IconsPage
      state={state}
      settings={settings}
      localstore={localstore}
      dispatch={dispatch}
      onCategoryChange={onCategoryChange}
      onSearch={onSearch}
      clearCatstore={clearCatstore}
      setStyleFilter={(style) => {
        setStyleFilterState(style);
        setLimit(1);
      }}
      loadMoreCategories={() => setLimit((current) => Math.min(current + 1, iconCategoryList.length))}
    />
  );
};

IconsPageContainer.propTypes = {
  localstore: PropTypes.object.isRequired,
  selectedNames: PropTypes.array.isRequired,
  settings: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.object,
  history: PropTypes.object,
};

export default connect((store) => ({
  localstore: store.localstore,
  selectedNames: store.catstore.catstore || [],
  settings: store.icoSettings,
}))(IconsPageContainer);
